import React, {useState, useEffect} from 'react';
import { Link } from 'react-router-dom';

import FullLayout from '../Layout/FullLayout';
import Loader from '../Core/Loader';
import Button from '../Core/Button';
import UserUploadModal from './UserFileUploadModal';

import { importedUsersSubject, clearImportedUsers, requestUsersPaginated, paginatedUsersResultSubject, requestPaginatedUserStatusSubject, createOrUpdateUsers, createOrUpdateUserStatusSubject } from '../../store/UserStore';
import useBehaviorSubject from '../../hooks/useBehaviorSubject';
import { institutionsSubject } from '../../store/InstitutionStore';
import { colorSubject, textThemaSubject } from '../../store/EnvironmentStore';

const UserListPage: React.SFC = () => {
    const [page, setPage] = useState(1);
    const [userSearch, setUserSearch] = useState('');
    const [showUserUploadModal, setShowUserUploadModal] = useState(false);
    const pageSize = 50;

    useEffect(() => {
        requestUsersPaginated(userSearch || null, page, pageSize);
    }, [page, userSearch]);

    const localImportedUsers = useBehaviorSubject(importedUsersSubject);
    const paginatedResult = useBehaviorSubject(paginatedUsersResultSubject);
    const createOrUpdateUserRequestStatus = useBehaviorSubject(createOrUpdateUserStatusSubject);
    const institutions = useBehaviorSubject(institutionsSubject);
    const color = useBehaviorSubject(colorSubject);
    const textThema = useBehaviorSubject(textThemaSubject);

    const areUsersFetching = (useBehaviorSubject(requestPaginatedUserStatusSubject) === 'PROCESSING')
        || createOrUpdateUserRequestStatus === 'PROCESSING';

    const newUsers = paginatedResult?.items ?? [];
    const totalCount = paginatedResult?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    return (
        <FullLayout>
            <Loader isLoading={areUsersFetching}/>
            <div className='bg-stone-100 pt-12 pb-24 grid'>
                <div className='w-content place-self-center bg-white p-6'>
                    <UserUploadModal
                        users={localImportedUsers}
                        show={showUserUploadModal}
                        createOrUpdateUsers={createOrUpdateUsers}
                        close={() => {setShowUserUploadModal(false); clearImportedUsers()}}
                        color={color}
                    />
                    <div className="clearfix" style={{ paddingBottom: 20 }}>
                        <div className="floatleft">
                            <p><label>User Search</label></p>
                            <p><input type="text" name="userSearch" value={userSearch} onChange={(ev) => setUserSearch(ev.target.value)} /></p>
                        </div>
                        <div className="floatright">
                            <Link to={`/users/add`}>
                                <Button _key={`add_user_button`} color={color} onClick={() => {}} style={{ marginRight: '10px' }}>
                                    Add User
                                </Button>
                            </Link>
                            <Button _key={`user_upload_button`} color={color} onClick={() => setShowUserUploadModal(true)}>
                                User Upload
                            </Button>
                        </div>
                    </div>
                    <table className="w-full">
                        <colgroup>
                            <col style={{ width: '30%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '25%' }} />
                            <col style={{ width: '17%' }} />
                        </colgroup>
                        <tbody className='divide-y divide-stone-300'>
                            <tr>
                                <th>Email</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Institution</th>
                                <th>Roles</th>
                            </tr>
                            {newUsers.map(user => {
                                const institution = institutions.find(i => user.institutionId === i.id);
                                return (
                                    <tr key={`user_${user.id}`}>
                                        <td><Link className={textThema} to={`/users/${user.id}`}>{user.email}</Link></td>
                                        <td><Link to={`/users/${user.id}`}>{user.firstName}</Link></td>
                                        <td><Link to={`/users/${user.id}`}>{user.lastName}</Link></td>
                                        <td>{institution ? institution.name : null}</td>
                                        <td>{user.roles.join(", ")}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    <div className="flex justify-between items-center mt-4">
                        <span>{totalCount} total users</span>
                        <div className="flex gap-2">
                            <Button
                                _key="prev_page"
                                color={color}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                style={{ opacity: page <= 1 ? 0.5 : 1 }}
                            >
                                Previous
                            </Button>
                            <span className="px-4 py-2">Page {page} of {totalPages}</span>
                            <Button
                                _key="next_page"
                                color={color}
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                style={{ opacity: page >= totalPages ? 0.5 : 1 }}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </FullLayout>
    );
}

export default UserListPage;
